import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from '@dr.pogodin/react-helmet';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useActiveOrg } from '@/hooks/useActiveOrg';

/**
 * /orgs/invitations/accept?token=...
 * Calls public.accept_org_invitation(_token) which inserts the user into
 * org_members and marks the invitation accepted. Requires the user to be
 * signed in with the same email the invitation was sent to.
 */
export default function OrgInvitationAccept() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { setActiveOrgId, refetch } = useActiveOrg();

  const [status, setStatus] = useState<'idle' | 'working' | 'done' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [orgId, setOrgId] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(`/auth?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      return;
    }
    if (!token) {
      setStatus('error');
      setError('Missing invitation token.');
      return;
    }
    if (status !== 'idle') return;
    setStatus('working');
    (async () => {
      const { data, error: rpcErr } = await supabase.rpc('accept_org_invitation', { _token: token });
      if (rpcErr) {
        setStatus('error');
        setError(rpcErr.message);
        toast.error(rpcErr.message);
        return;
      }
      const newOrgId = data as unknown as string;
      setOrgId(newOrgId);
      setActiveOrgId(newOrgId);
      await refetch();
      setStatus('done');
      toast.success('You joined the organization');
    })();
  }, [authLoading, user, token, status, navigate, setActiveOrgId, refetch]);

  return (
    <div className="container max-w-md py-16">
      <Helmet>
        <title>Accept invitation</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Card>
        <CardContent className="py-10 text-center space-y-4">
          {status === 'working' && (
            <>
              <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto" />
              <p className="text-sm text-muted-foreground">Accepting invitation…</p>
            </>
          )}
          {status === 'done' && orgId && (
            <>
              <h1 className="text-xl font-semibold">You're in 🎉</h1>
              <p className="text-sm text-muted-foreground">Welcome to your new organization.</p>
              <Button onClick={() => navigate(`/orgs/${orgId}`)}>Go to organization</Button>
            </>
          )}
          {status === 'error' && (
            <>
              <h1 className="text-xl font-semibold">Couldn't accept invitation</h1>
              <p className="text-sm text-muted-foreground">{error}</p>
              <Button variant="outline" onClick={() => navigate('/orgs')}>My organizations</Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
